import React, { useEffect, useState } from "react";

const regularSteps = [
  {
    selector: '[data-tour="pickup-card"]',
    title: "Review each pickup card",
    body: "Each card has the rider name, phone number, group size, and pickup address. Phone numbers and addresses are clickable.",
  },
  {
    selector: '[data-tour="pickup-time"]',
    title: "Set pickup times",
    body: "Tap the time button beside a rider's name to choose or edit their pickup time.",
  },
  {
    selector: '[data-tour="status-steps"]',
    title: "Update rider status",
    body: "Use the Pending, On way, and Arrived steps to keep riders updated by text.",
  },
  {
    selector: '[data-tour="view-map"]',
    title: "View pickups on the map",
    body: "Use View Map to see pickup locations together when rider addresses have coordinates.",
  },
  {
    selector: '[data-tour="optimize-route"]',
    title: "Optimize the route",
    body: "Use Optimize to calculate a more efficient pickup order and apply suggested pickup times.",
  },
];

const shuttleSteps = [
  {
    selector: '[data-tour="pickup-card"]',
    title: "Review each pickup spot",
    body: "Each card shows a pickup location, address, pickup time, assigned students, and the driver status for that stop.",
  },
  {
    selector: '[data-tour="pickup-time"]',
    title: "Check pickup times",
    body: "Use the pickup time shown on each stop card to know when to arrive at that location.",
  },
  {
    selector: '[data-tour="status-steps"]',
    title: "Update shuttle status",
    body: "Use the stop status buttons to tell students and admins when you are on route, have arrived, departed, or reached the event.",
  },
  {
    selector: '[data-tour="location-sharing"]',
    title: "Share your location",
    body: "Turn on location sharing so admins can see your latest location and refresh ETA information while you are on route.",
  },
  {
    selector: '[data-tour="view-map"]',
    title: "Open addresses in Maps",
    body: "Tap the destination or pickup address to open it in Google Maps.",
  },
];

const PickupGuideModal = ({ isOpen, onClose, mode = "regular" }) => {
  const steps = mode === "shuttle" ? shuttleSteps : regularSteps;
  const [currentStep, setCurrentStep] = useState(0);
  const [availableSteps, setAvailableSteps] = useState(steps);
  const [highlightRect, setHighlightRect] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    const visibleSteps = steps.filter((step) =>
      document.querySelector(step.selector),
    );
    setAvailableSteps(visibleSteps.length ? visibleSteps : steps.slice(0, 1));
    setCurrentStep(0);
  }, [isOpen, steps]);

  useEffect(() => {
    if (!isOpen) return;

    const step = availableSteps[currentStep];
    const element = step && document.querySelector(step.selector);

    const updateHighlight = () => {
      if (!element) return;

      const rect = element.getBoundingClientRect();
      setHighlightRect({
        top: Math.max(rect.top - 8, 8),
        left: Math.max(rect.left - 8, 8),
        width: Math.min(rect.width + 16, window.innerWidth - 16),
        height: rect.height + 16,
      });
    };

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    const timeoutId = setTimeout(updateHighlight, 350);
    window.addEventListener("resize", updateHighlight);
    window.addEventListener("scroll", updateHighlight, true);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateHighlight);
      window.removeEventListener("scroll", updateHighlight, true);
    };
  }, [availableSteps, currentStep, isOpen]);

  if (!isOpen) return null;

  const isLastStep = currentStep === availableSteps.length - 1;
  const step = availableSteps[currentStep] || steps[0];
  const tooltipWidth = Math.min(360, window.innerWidth - 24);
  const tooltipTop = highlightRect
    ? highlightRect.top + highlightRect.height + 14 > window.innerHeight - 220
      ? Math.max(12, highlightRect.top - 226)
      : highlightRect.top + highlightRect.height + 14
    : window.innerHeight - 260;
  const tooltipLeft = highlightRect
    ? Math.min(
        Math.max(
          12,
          highlightRect.left + highlightRect.width / 2 - tooltipWidth / 2,
        ),
        window.innerWidth - tooltipWidth - 12,
      )
    : 12;

  const handleNext = () => {
    if (isLastStep) {
      onClose();
      return;
    }

    setCurrentStep((stepIndex) => stepIndex + 1);
  };

  return (
    <div className="fixed inset-0 z-[60] pointer-events-none">
      {highlightRect && (
        <div
          className="fixed rounded-2xl ring-4 ring-white transition-all duration-300"
          style={{
            ...highlightRect,
            boxShadow: "0 0 0 9999px rgba(17, 24, 39, 0.68)",
          }}
        />
      )}

      <div
        className="fixed pointer-events-auto bg-white rounded-2xl p-5 shadow-2xl transition-all duration-300"
        style={{ top: tooltipTop, left: tooltipLeft, width: tooltipWidth }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#004a7e]">
              Pickup page guide · Step {currentStep + 1} of{" "}
              {availableSteps.length}
            </p>
            <h2 className="mt-1 text-xl font-bold text-gray-900">
              {step.title}
            </h2>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-circle btn-ghost"
            onClick={onClose}
            aria-label="Close pickup page guide"
          >
            ✕
          </button>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-gray-600">
          {step.body}
        </p>

        <div className="mt-5 flex gap-2">
          {availableSteps.map((item, index) => (
            <div
              key={item.title}
              className={`h-2 flex-1 rounded-full ${
                index <= currentStep ? "bg-[#004a7e]" : "bg-gray-200"
              }`}
            />
          ))}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            type="button"
            className="btn btn-outline"
            onClick={
              currentStep === 0
                ? onClose
                : () => setCurrentStep((stepIndex) => stepIndex - 1)
            }
          >
            {currentStep === 0 ? "Skip" : "Back"}
          </button>
          <button
            type="button"
            className="btn bg-[#004a7e] text-white hover:bg-[#003a64]"
            onClick={handleNext}
          >
            {isLastStep ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PickupGuideModal;
